/**
 * Workspace form (S03) — founder + email + company + state + optional GSTIN.
 *
 * GSTIN entry triggers a debounced `onboarding.lookupGstin` query; on success
 * the result card is shown beneath the field and `legalName`/`tradeName`/`state`
 * are auto-filled (one-shot — the user can still override).
 *
 * On submit, `identity.createWorkspace` returns `{companyId, trialEndsOn}`;
 * the slice stores them and we navigate to `/onboarding/tally`.
 *
 * @see prototype/onboarding/gstin.html — S03 baseline
 */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardBody,
  Form,
  TextField,
  SelectField,
  Submit,
  SubmitProbe,
  MetaPill,
} from "@flint/ui";
import { identity, indianState } from "@flint/api-contract";
import { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryKey, meQueryOptions } from "../../../lib/session.ts";
import { useOnboardingFlow } from "../lib/flow-state.ts";
import { CompanyResult } from "./company-result.tsx";
import { TrialCallout } from "./trial-callout.tsx";

type FormValues = z.infer<typeof identity.createWorkspaceInput>;

/**
 * Form-side schema. The wire schema (`identity.createWorkspaceInput`) is
 * the authority on the API shape; this shim wraps it so empty strings on
 * optional fields (`tradeName`, `gstin`) are accepted. Without it, RHF's
 * zodResolver rejects the controlled-input default `''` against `gstin.regex`,
 * blocking legitimate submissions where the founder doesn't have a GSTIN.
 *
 * Pattern: `preprocess(emptyToUndef, innerOptional)` — the inner schema's
 * own `.optional()` swallows the undefined produced by preprocess.
 */
const emptyToUndef = (v: unknown) => (typeof v === "string" && v.trim() === "" ? undefined : v);

const formSchema = identity.createWorkspaceInput.extend({
  tradeName: z.preprocess(emptyToUndef, z.string().max(255).optional()),
  gstin: z.preprocess(emptyToUndef, identity.createWorkspaceInput.shape.gstin.unwrap().optional()),
});

const STATE_OPTIONS = indianState.options.map((s) => ({ value: s, label: s }));

export function WorkspaceForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setWorkspace = useOnboardingFlow((s) => s.setWorkspace);

  // Prefill founder name + work email from the prior `/onboarding/account`
  // step (ADR-0011 §3.5). The root guard guarantees `profileComplete`
  // by the time we reach this page, so both fields are populated when
  // `me` is loaded. Falls back to empty strings while the query is in
  // flight so the controlled inputs stay valid.
  const me = useQuery(meQueryOptions);
  const meName = me.data?.user.name ?? "";
  const mePhone = me.data?.user.phone ?? "";
  const meEmail = me.data?.user.email ?? "";
  // Defensive: if `name === phone` (placeholder), don't surface the
  // phone digits as the founder's display name. The guard should
  // prevent this state, but we belt-and-brace.
  const founderNameDefault = meName && meName !== mePhone ? meName : "";

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      founderName: founderNameDefault,
      legalName: "",
      tradeName: "",
      workEmail: meEmail,
      state: "Maharashtra",
      gstin: "",
    },
  });

  // Re-sync defaults if `me` lands after the form mounted (e.g. cold
  // load). RHF's `reset` only fires for actually-different values.
  useEffect(() => {
    const current = form.getValues();
    if (!current.founderName && founderNameDefault) {
      form.setValue("founderName", founderNameDefault);
    }
    if (!current.workEmail && meEmail) {
      form.setValue("workEmail", meEmail);
    }
  }, [founderNameDefault, meEmail, form]);

  const gstin = form.watch("gstin");
  const debouncedGstin = useDebounced(gstin, 350);
  const isFormatComplete = (debouncedGstin ?? "").length === 15;

  const lookup = trpc.onboarding.lookupGstin.useQuery(
    { gstin: debouncedGstin ?? "" },
    { enabled: isFormatComplete, retry: false, staleTime: 60_000 },
  );

  // One-shot autofill when a lookup succeeds: only fill blank fields.
  useEffect(() => {
    if (!lookup.data) return;
    const v = form.getValues();
    if (!v.legalName) {
      form.setValue("legalName", lookup.data.legalName, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (!v.tradeName) {
      form.setValue("tradeName", lookup.data.tradeName ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    form.setValue("state", lookup.data.state, { shouldDirty: true, shouldValidate: true });
    form.clearErrors(["legalName", "tradeName", "state"]);
  }, [lookup.data, form]);

  const createWorkspace = trpc.identity.createWorkspace.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    // Optional fields default to '' on the form (RHF can't unset a controlled
    // input) but the wire schema rejects '' against `gstin.regex` /
    // `tradeName.max`. Strip empty optionals before sending.
    const payload: typeof values = {
      founderName: values.founderName.trim(),
      legalName: values.legalName.trim(),
      workEmail: values.workEmail.trim(),
      state: values.state,
      ...(values.tradeName?.trim() ? { tradeName: values.tradeName.trim() } : {}),
      ...(values.gstin?.trim() ? { gstin: values.gstin.trim().toUpperCase() } : {}),
    };
    const result = await createWorkspace.mutateAsync(payload);
    setWorkspace({ companyId: result.companyId, trialEndsOn: result.trialEndsOn });
    // The membership now exists; the cached `me` (company: null) needs to
    // refresh before the next navigation, otherwise the root guard will
    // bounce the user back to this same page. We `refetchQueries` (not
    // `invalidateQueries`): the latter only refetches active subscribers and
    // there are none on this page, so the guard's `ensureQueryData` would
    // return the still-cached `company: null` and redirect right back here.
    await queryClient.refetchQueries({ queryKey: meQueryKey });
    void navigate({ to: "/onboarding/tally" });
  });

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-3.5">
              <div className="flex flex-wrap gap-1.5">
                <MetaPill>Founder workspace</MetaPill>
                <MetaPill>Optional GST autofill</MetaPill>
                <MetaPill>14-day trial</MetaPill>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField
                  name="founderName"
                  label="Founder name"
                  placeholder="Rakesh Sharma"
                  autoComplete="name"
                  // Captured one screen earlier on /onboarding/account. We
                  // prefill + lock to keep the data path single-source; the
                  // user can still change it via the link below.
                  readOnly={!!founderNameDefault}
                />
                <TextField
                  name="workEmail"
                  label="Work email"
                  placeholder="you@company.com"
                  type="email"
                  autoComplete="email"
                  readOnly={!!meEmail}
                />
                {founderNameDefault || meEmail ? (
                  <p className="sm:col-span-2 -mt-2 text-xs text-muted">
                    Filled from the previous step.{" "}
                    <Link
                      to="/onboarding/account"
                      className="font-medium text-sky-600 hover:underline"
                    >
                      Edit your account
                    </Link>
                    .
                  </p>
                ) : null}
                <div className="sm:col-span-2">
                  <TextField
                    name="legalName"
                    label="Company name"
                    placeholder="Sharma Industries Pvt Ltd"
                    autoComplete="organization"
                  />
                </div>
                <TextField name="tradeName" label="Trade name" placeholder="Sharma Industries" />
                <SelectField name="state" label="State" options={STATE_OPTIONS} />
                <div className="sm:col-span-2">
                  <TextField
                    name="gstin"
                    label="GSTIN (optional)"
                    placeholder="27ABCDE1234F1Z5"
                    helper="Add now to pull legal business details and tax defaults. You can continue without it."
                    maxLength={15}
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck={false}
                    className="font-mono uppercase tracking-wide"
                    onChange={(e) => {
                      const v = e.currentTarget.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 15);
                      form.setValue("gstin", v, { shouldValidate: false });
                    }}
                  />
                </div>
              </div>

              {isFormatComplete && lookup.isFetching ? (
                <div className="p-3 rounded bg-bg ring-1 ring-line text-xs text-muted flex items-center gap-2">
                  <Spinner />
                  <span>Looking up GSTIN with the GST registry…</span>
                </div>
              ) : null}

              {isFormatComplete && lookup.data ? (
                <CompanyResult gstin={debouncedGstin ?? ""} company={lookup.data} />
              ) : null}

              {isFormatComplete && lookup.error ? (
                <div className="p-3 rounded bg-bad-50 text-xs text-bad ring-1 ring-bad-100/60">
                  GSTIN not found. You can still continue without it.
                </div>
              ) : null}

              <TrialCallout />

              <Submit pendingLabel="Creating workspace" disabled={createWorkspace.isPending}>
                Create workspace
              </Submit>

              {createWorkspace.error ? (
                <p role="alert" className="text-xs text-bad">
                  {createWorkspace.error.message}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return useMemo(() => v, [v]);
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 rounded-full border-2 border-line border-t-sky-600 animate-spin"
    />
  );
}
